// The workflow run API on the agent harness (`/api/v1/workflow-harness`,
// backend/web/src/workflow-harness/workflow-harness.controller.ts), behind `workflow_harness_enabled`. Same client and
// stream handling as the engine's api (../v4/api.ts); the stream carries the model's text as it is written.
import { apiService } from '@/service/api';
import { readSSEMessages } from '@/utils/sse';

const BASE = 'v1/workflow-harness';
const runPath = (runId: string, suffix = '') => `${BASE}/runs/${encodeURIComponent(runId)}${suffix}`;

/** The engine's typed ask contract, with the single-use resume token that answers it. */
export interface HarnessAsk {
  ask_id: string;
  kind: string;
  question: string;
  reason: string;
  attempted: string[];
  options: { value: string; label: string }[];
  safe_default: string | null;
  resume_token: string;
}

export interface HarnessAction {
  action_id: string;
  kind: 'alert' | 'work_order';
  title: string;
  body: string;
}

export interface HarnessEvent {
  seq: number;
  at: string;
  type: string;
  payload?: Record<string, unknown>;
}

/** `status` says where the run is; `outcome` how a finished run ended. */
export interface HarnessRun {
  runId: string;
  question: string;
  status: 'planning' | 'awaiting_approval' | 'awaiting_ask' | 'running' | 'finished';
  outcome: 'satisfied' | 'partial' | 'blocked' | 'cancelled' | null;
  phase: 'plan' | 'execute';
  plan: string | null;
  answer: string | null;
  openAsk: HarnessAsk | null;
  actions: HarnessAction[];
  events: HarnessEvent[];
  audit: { checked: number; untraced: string[]; toolCalls: number } | null;
  error: string | null;
  model: string | null;
  effort: string | null;
}

/** The URL parameter that names the run on screen, so a refresh or a link comes back to it. */
export const RUN_PARAM = 'harnessRun';

/** Whether a turn is running: the model's text and steps stream in. */
export const working = (run: Pick<HarnessRun, 'status'>) => run.status === 'planning' || run.status === 'running';

export type LiveFrame =
  | { type: 'text'; delta: string }
  | { type: 'step'; tool: string }
  | { type: 'action'; action: HarnessAction }
  | { type: 'state' };

async function post<T>(path: string, body: object): Promise<T> {
  const res = await apiService.fetchOrError(apiService.getUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await res.json()) as T;
}

const RETRY_MS = 3_000;
const INACTIVITY_MS = 60_000;

export const harnessApi = {
  start: (question: string) => post<{ runId: string }>(`${BASE}/runs`, { question }),
  run: async (runId: string) => (await apiService.fetchOrError(apiService.getUrl(runPath(runId)))).json() as Promise<HarnessRun>,
  approve: (runId: string, note?: string) => post(runPath(runId, '/approve'), note ? { note } : {}),
  answer: (resumeToken: string, answer: string) => post<{ runId: string }>(`${BASE}/asks/answer`, { resumeToken, answer }),
  cancel: (runId: string) => post(runPath(runId, '/cancel'), {}),
  /** Calls back with every live frame; reopens a dropped stream and sends `state` on reopening. Returns a stop function. */
  watch(runId: string, onFrame: (frame: LiveFrame) => void): () => void {
    const controller = new AbortController();
    const { signal } = controller;
    void (async () => {
      let opened = false;
      while (!signal.aborted) {
        try {
          const res = await apiService.fetchOrError(
            apiService.getUrl(runPath(runId, '/stream')),
            { headers: { Accept: 'text/event-stream' }, signal },
            { timeoutMs: null },
          );
          if (opened) onFrame({ type: 'state' });
          opened = true;
          for await (const message of readSSEMessages(res, { inactivityTimeoutMs: INACTIVITY_MS })) {
            if (signal.aborted) return;
            if (message.data) onFrame(JSON.parse(message.data) as LiveFrame);
          }
        } catch (error) {
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (signal.aborted || (typeof status === 'number' && status < 500 && status !== 408 && status !== 429)) return;
        }
        await new Promise<void>((resolve) => {
          const timer = setTimeout(resolve, RETRY_MS);
          signal.addEventListener('abort', () => {
            clearTimeout(timer);
            resolve();
          });
        });
      }
    })();
    return () => controller.abort();
  },
};
